import { useMemo, type JSX } from 'react';
import { matchRoutes, useParams, useRoutes } from 'react-router-dom';
import { Entity } from '@backstage/catalog-model';
import {
  EntityContentGroupDefinitions,
  EntityHeaderLayoutProps,
} from '@backstage/plugin-catalog-react/alpha';
import { trimTrailingSlashes } from '../shared/links';

/** What a tab's runtime guard knows about its data (AC-7). */
export type TabAvailability = 'yes' | 'no' | 'unknown';

/** One entity content (tab) as the entity page receives it. */
export type EntityRoute = {
  path: string;
  title: string;
  group?: string;
  if?: (entity: Entity) => boolean;
  /**
   * Runtime no-data guard, a hook: the tab shows once it says `yes`, and a
   * `no` hides it and sends its URL to Visão geral (D-13). While `unknown`
   * (loading only; a failed load or a guard that throws is `no`) the tab
   * stays out of the bar but its URL still opens it.
   */
  useAvailability?: (entity: Entity) => TabAvailability;
  children: JSX.Element;
};

export type EntityTab = EntityHeaderLayoutProps['tabs'][number];
export type EntityTabItem = { id: string; label: string; href: string };

/**
 * Tab groups of the v2 entity pages, in their order (UI spec "Tab bar"):
 * Visão geral · Dependências · Docs · Deploys · Gateway · Infraestrutura ·
 * API on components; Visão geral · Definição · Gateway on APIs. Contents
 * join a tab through their `group`.
 */
export const TAB_GROUP_ORDER = [
  'overview',
  'definition',
  'dependencies',
  'documentation',
  'deployment',
  'gateway',
  'infrastructure',
  'api',
] as const;

export function isGroupTab(
  tab: EntityTab,
): tab is Extract<EntityTab, { items: unknown }> {
  return 'items' in tab;
}

/** The contents whose filter accepts the entity, in their given order. */
export function visibleRoutes(
  routes: EntityRoute[],
  entity: Entity | undefined,
): EntityRoute[] {
  if (!entity) {
    return [];
  }
  return routes.filter(route => !route.if || route.if(entity));
}

/**
 * Applies the runtime guards: the routes that can open (every one whose
 * guard did not say `no`) and the routes the tab bar lists (no guard, or a
 * guard that said `yes`).
 */
export function guardedRoutes(
  routes: EntityRoute[],
  availability: (path: string) => TabAvailability | undefined,
): { routable: EntityRoute[]; tabbed: EntityRoute[] } {
  const routable = routes.filter(
    route => !route.useAvailability || availability(route.path) !== 'no',
  );
  const tabbed = routable.filter(
    route => !route.useAvailability || availability(route.path) === 'yes',
  );
  return { routable, tabbed };
}

/**
 * A content's filter from its extension data: the filter function when
 * there is one, else the deprecated string expression.
 *
 * Mirrors, in part, `buildFilterFn` of the stock entity page
 * (`@backstage/plugin-catalog/dist/alpha/pages.esm.js`). Only `kind:` and
 * `type:` terms (comma lists, all terms must match) are understood.
 *
 * Fails closed (code O3): an expression with any other term — `is:orphan`,
 * `has:links`, a typo — never matches, so the content is hidden rather than
 * shown on the wrong entity. This is deliberate and silent: nothing is
 * logged, since the expression is configuration, not user input. No
 * installed content uses a string filter today.
 */
export function contentFilter(
  filterFunction?: (entity: Entity) => boolean,
  filterExpression?: string,
): ((entity: Entity) => boolean) | undefined {
  if (filterFunction) {
    return filterFunction;
  }
  if (!filterExpression?.trim()) {
    return undefined;
  }
  const terms = filterExpression.trim().split(/\s+/);
  return entity =>
    terms.every(term => {
      const [key, values = ''] = term.split(':');
      const accepted = values
        .split(',')
        .map(value => value.toLocaleLowerCase('en-US'))
        .filter(Boolean);
      let actual: unknown;
      if (key === 'kind') {
        actual = entity.kind;
      } else if (key === 'type') {
        actual = entity.spec?.type;
      } else {
        return false;
      }
      return (
        typeof actual === 'string' &&
        accepted.includes(actual.toLocaleLowerCase('en-US'))
      );
    });
}

/** "/deps/*" → "deps"; "/" → "". */
function tabHref(path: string): string {
  return trimTrailingSlashes(path.replace(/\/\*$/, '').replace(/^\/+/, ''));
}

function resolveGroup(
  group: string | undefined,
  definitions: EntityContentGroupDefinitions,
): string | undefined {
  if (!group) {
    return undefined;
  }
  if (
    definitions[group] ||
    (TAB_GROUP_ORDER as readonly string[]).includes(group)
  ) {
    return group;
  }
  return Object.entries(definitions).find(([, definition]) =>
    definition.aliases?.includes(group),
  )?.[0];
}

/**
 * The header tabs: one per group (known groups first, in the v2 order, then
 * the configured ones, then contents without a group in their own order).
 *
 * Mirrors `buildHeaderTabs` of the stock entity page
 * (`@backstage/plugin-catalog/dist/alpha/components/EntityTabs/useEntityTabs.esm.js`),
 * with the v2 order instead of `contentOrder` sorting.
 *
 * A group with several contents becomes one tab with items, labelled by
 * `groupLabel`, else the configured group title (the stock label). A lone
 * content keeps its own (stock) title, unless `groupLabel` gives its v2
 * group a label. Pass a `groupLabel` that returns undefined to keep every
 * stock title: the entity page does so on kinds other than Component (F6,
 * until Sprint 3 redesigns them).
 */
export function buildEntityTabs(
  routes: EntityRoute[],
  definitions: EntityContentGroupDefinitions,
  groupLabel: (groupId: string) => string | undefined,
): EntityTab[] {
  const buckets = new Map<string, { group?: string; items: EntityTabItem[] }>();
  for (const route of routes) {
    const group = resolveGroup(route.group, definitions);
    const key = group ?? `route:${route.path}`;
    const bucket = buckets.get(key) ?? { group, items: [] };
    bucket.items.push({
      id: route.path,
      label: route.title,
      href: tabHref(route.path),
    });
    buckets.set(key, bucket);
  }

  const configured = Object.keys(definitions);
  const rank = (group?: string) => {
    if (!group) {
      return Number.MAX_SAFE_INTEGER;
    }
    const known = (TAB_GROUP_ORDER as readonly string[]).indexOf(group);
    if (known !== -1) {
      return known;
    }
    const index = configured.indexOf(group);
    return index === -1
      ? Number.MAX_SAFE_INTEGER - 1
      : TAB_GROUP_ORDER.length + index;
  };
  // Array sort is stable: equal ranks keep the contents' own order.
  const ordered = [...buckets.entries()].sort(
    ([, a], [, b]) => rank(a.group) - rank(b.group),
  );

  return ordered.map(([key, bucket]) => {
    const v2Label = bucket.group ? groupLabel(bucket.group) : undefined;
    if (bucket.items.length > 1 && bucket.group) {
      const label = v2Label ?? definitions[bucket.group]?.title;
      return { id: key, label: label || bucket.group, items: bucket.items };
    }
    const [item] = bucket.items;
    const known =
      bucket.group &&
      (TAB_GROUP_ORDER as readonly string[]).includes(bucket.group);
    return { ...item, label: (known && v2Label) || item.label };
  });
}

function normalizeRoutePath(path: string): string {
  const withoutLeading = path.replace(/^\/+/, '');
  if (withoutLeading.endsWith('*')) {
    return withoutLeading;
  }
  const trimmed = trimTrailingSlashes(withoutLeading);
  return trimmed ? `${trimmed}/*` : '';
}

/**
 * The content matching the current sub-path, as in the stock entity page:
 * its element (or undefined when nothing matches) and its route.
 *
 * Mirrors `normalizeRoutePath` and `useSelectedSubRoute` of
 * `@backstage/plugin-catalog/dist/alpha/components/EntityTabs/EntityTabs.esm.js`.
 * The drift guard (`stockMirror.test.ts`) fails when that file changes.
 */
export function useSelectedContent(routes: EntityRoute[]): {
  element?: JSX.Element;
  route?: EntityRoute;
} {
  const params = useParams();
  const routeObjects = useMemo(
    () =>
      routes.map(({ path, children }) => ({
        caseSensitive: false,
        path: normalizeRoutePath(path),
        element: children,
      })),
    [routes],
  );
  const element = useRoutes(routeObjects) ?? undefined;
  let current = params['*'] ?? '';
  if (!current.startsWith('/')) {
    current = `/${current}`;
  }
  const [matched] = matchRoutes(routeObjects, current) ?? [];
  const index = matched
    ? routeObjects.findIndex(route => route.path === matched.route.path)
    : -1;
  return { element, route: index === -1 ? undefined : routes[index] };
}
